// File: cgp-authentication.js
// Author: Brandon Shreve
// Date: 1/9/2017
// Desc: Authentication module that handles integrating with CGP Authentication.
//       Handles redirecting to GAME login page and reading the cookie that has our authorization token.
//       Has methods to get the authorization token as well as the logged in user's information.
// Other Contributors: Joseph M Diaz

import { config } from '../config/config';
import { cgpCookieHelper } from './cgp-cookie-helper';
import { cgpLoginUrls, cgpSecurityUrls } from '../../src/config/config.base';
import { getEnv, setUserProfile, getUserProfile } from './utility';

function updateLoadingMessage(message: string) {
    const x = document.getElementById('loading').firstElementChild;
    x.innerHTML = `${message}...`;
}

function singleSignOnRedirect(redirectLink: Location) {
    updateLoadingMessage('Redirecting to GAME');
    const cgpSecurityUrl = cgpLoginUrls[getEnv()] + redirectLink;
    window.location.replace(cgpSecurityUrl);
}

function validateToken(callback: Function) {
    updateLoadingMessage('Authenticating');
    const token = getAuthToken();

    if (token) {
        const url = cgpSecurityUrls[getEnv()] + 'validateToken';
        const http = new XMLHttpRequest();

        http.open('POST', url, true);
        http.setRequestHeader('Content-type', 'application/json');
        http.setRequestHeader('Authorization', token);

        http.onreadystatechange = () => handleReadyStateChange(http, callback);
        http.send();
    } else {
        singleSignOnRedirect(document.location);
    }
}

export const handleReadyStateChange = (http: XMLHttpRequest, callback) => {
    if (http.readyState !== XMLHttpRequest.DONE) {
        return;
    }
    if (http.status === 200) {
        const newToken = http.getResponseHeader(config.authentication.authenticationCookie);
        cgpCookieHelper.setAuthCookie(newToken.replace(/^\s*Bearer\s*/, ''));
        if (!getUserInfo()) {
            const profile = http.getResponseHeader('Userinfo');
            setUserProfile(JSON.parse(profile));
        }
        if (callback) { callback(); }
    } else {
        cgpCookieHelper.deleteAuthCookie();
        singleSignOnRedirect(document.location);
    }
};

function refreshToken(callback: Function) {
    const token = getAuthToken();
    const url = cgpSecurityUrls[getEnv()] + 'refreshToken';
    const http = new XMLHttpRequest();

    http.open('POST', url, true);
    http.setRequestHeader('Content-type', 'application/json');
    http.setRequestHeader('Authorization', token);

    http.onreadystatechange = () => handleReadyStateChange(http, callback);
    http.send();
}

function invalidateToken(callback: Function) {
    cgpCookieHelper.deleteAuthCookie();
}

function parseToken(token) {
    if (token) {
        const parsedToken = token.replace('Bearer_', 'Bearer ');
        return parsedToken;
    } else {
        return '';
    }
}

function getAuthToken() {
    const token = cgpCookieHelper.retrieveCookie(config.authentication.authenticationCookie);
    return parseToken(token);
}

function getUserInfo() {
    const userInfo = cgpCookieHelper.retrieveCookie(config.authentication.userInfoCookie);

    if (userInfo) {
        const profile = JSON.parse(JSON.parse(userInfo));
        const userProfile = getUserProfile();
        if (!userProfile) {
            setUserProfile(profile);
        }
        return profile;
    } else {
        return getUserProfile();
    }
}

export const cgpAuthentication = {
    singleSignOnRedirect: singleSignOnRedirect,
    getAuthToken: getAuthToken,
    getUserInfo: getUserInfo,
    validateToken: validateToken,
    invalidateToken: invalidateToken,
    refreshToken: refreshToken,
    updateLoadingMessage
};
